import { CalendarDays, ChevronLeft, ChevronRight, List } from "lucide-react";
import {
  useGetList,
  useTranslate,
  useLocaleState,
  type Identifier,
} from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";

import { ListViewToggle } from "../misc/ListViewToggle";
import { useListView } from "../misc/useListView";
import type { CrmEvent } from "../types";
import {
  addMonths,
  getDefaultEventStart,
  getMonthGridRange,
  startOfMonth,
} from "./calendarUtils";
import { EventCalendar } from "./EventCalendar";
import { EventCreateDialog, EventEditDialog } from "./EventDialogs";
import { EventList } from "./EventList";

type EventsView = "calendar" | "list";

const VIEW_STORE_KEY = "events.listView";
const MAX_EVENTS_PER_MONTH = 500;

/** « Événements » page: month calendar or list of the venue's events. */
export const EventsPage = () => {
  const translate = useTranslate();
  const [view] = useListView<EventsView>(VIEW_STORE_KEY, "calendar");
  const [createDefaults, setCreateDefaults] =
    useState<Partial<CrmEvent> | null>(null);
  const [selectedId, setSelectedId] = useState<Identifier | null>(null);
  const createOn = (day: Date) =>
    setCreateDefaults({ start_at: getDefaultEventStart(day) });

  return (
    <div className="flex flex-col gap-4 mt-4">
      <div className="flex flex-wrap items-center gap-3">
        <CalendarDays className="text-muted-foreground w-6 h-6" />
        <h1 className="text-2xl font-semibold flex-1">
          {translate("resources.events.name", { smart_count: 2 })}
        </h1>
        <ListViewToggle<EventsView>
          storeKey={VIEW_STORE_KEY}
          defaultView="calendar"
          options={[
            {
              value: "calendar",
              icon: <CalendarDays />,
              label: "resources.events.views.calendar",
            },
            {
              value: "list",
              icon: <List />,
              label: "resources.events.views.list",
            },
          ]}
        />
        <Button className="cursor-pointer" onClick={() => createOn(new Date())}>
          {translate("resources.events.action.create")}
        </Button>
      </div>
      {view === "calendar" ? (
        <CalendarView
          onCreate={createOn}
          onSelect={(event) => setSelectedId(event.id)}
        />
      ) : (
        <EventList onSelect={(event) => setSelectedId(event.id)} />
      )}
      <EventCreateDialog
        open={createDefaults != null}
        record={createDefaults ?? {}}
        onClose={() => setCreateDefaults(null)}
      />
      <EventEditDialog
        eventId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
};

const CalendarView = ({
  onCreate,
  onSelect,
}: {
  onCreate: (day: Date) => void;
  onSelect: (event: CrmEvent) => void;
}) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const { start, end } = getMonthGridRange(month);
  const { data: events = [] } = useGetList<CrmEvent>("events", {
    filter: {
      "start_at@gte": start.toISOString(),
      "start_at@lt": end.toISOString(),
    },
    pagination: { page: 1, perPage: MAX_EVENTS_PER_MONTH },
    sort: { field: "start_at", order: "ASC" },
  });
  const monthLabel = new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  }).format(month);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="cursor-pointer"
          onClick={() => setMonth(startOfMonth(new Date()))}
        >
          {translate("resources.events.calendar.today")}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-8 cursor-pointer"
          aria-label={translate("resources.events.calendar.previous_month")}
          onClick={() => setMonth(addMonths(month, -1))}
        >
          <ChevronLeft />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-8 cursor-pointer"
          aria-label={translate("resources.events.calendar.next_month")}
          onClick={() => setMonth(addMonths(month, 1))}
        >
          <ChevronRight />
        </Button>
        <h2 className="text-lg font-medium capitalize">{monthLabel}</h2>
      </div>
      <EventCalendar
        month={month}
        events={events}
        onCreate={onCreate}
        onSelect={onSelect}
      />
    </div>
  );
};
